import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Chip
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import apiClient from '../../services/api'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'



// Schema de validare
const schema = yup.object().shape({
  name: yup
    .string()
    .required('Numele este obligatoriu')
    .min(3, 'Numele trebuie să aibă minim 3 caractere')
    .max(100, 'Numele nu poate depăși 100 de caractere'),
  tenant_id: yup
    .string()
    .required('Tenant-ul este obligatoriu'),
  dicom_institution_name: yup
    .string()
    .required('Numele DICOM este obligatoriu')
    .max(50, 'Numele DICOM nu poate depăși 50 de caractere'),
  address: yup
    .string()
    .max(200, 'Adresa nu poate depăși 200 de caractere'),
  phone: yup
    .string()
    .max(20, 'Telefonul nu poate depăși 20 de caractere'),
  email: yup
    .string()
    .email('Email invalid')
    .max(100, 'Email-ul nu poate depăși 100 de caractere'),
  website: yup
    .string()
    .url('URL invalid')
    .max(100, 'Website-ul nu poate depăși 100 de caractere'),
  institution_type: yup
    .string()
    .required('Tipul instituției este obligatoriu'),
  timezone: yup
    .string()
    .default('Europe/Bucharest'),
  default_language: yup
    .string()
    .default('ro'),
  active: yup
    .boolean()
    .default(true)
})

const institutionTypes = [
  { value: 'hospital', label: 'Spital' },
  { value: 'clinic', label: 'Clinică' },
  { value: 'cabinet', label: 'Cabinet' }
]

const timezones = [
  { value: 'Europe/Bucharest', label: 'România (București)' },
  { value: 'Europe/London', label: 'Marea Britanie (Londra)' },
  { value: 'Europe/Paris', label: 'Franța (Paris)' },
  { value: 'Europe/Berlin', label: 'Germania (Berlin)' }
]

const languages = [
  { value: 'ro', label: 'Română' },
  { value: 'en', label: 'Engleză' },
  { value: 'fr', label: 'Franceză' },
  { value: 'de', label: 'Germană' }
]

interface InstitutionFormProps {
  open: boolean
  onClose: () => void
  institution?: any
  onSave: () => void
}

export const InstitutionForm: React.FC<InstitutionFormProps> = ({
  open,
  onClose,
  institution,
  onSave
}) => {
  const { user } = useAuth()
  const [saving, setSaving] = useState(false)
  const [autoSaveTimer, setAutoSaveTimer] = useState<ReturnType<typeof setTimeout> | null>(null)
  const [draftSaved, setDraftSaved] = useState(false)
  const [tenants, setTenants] = useState<any[]>([])
  const [loadingTenants, setLoadingTenants] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      tenant_id: '',
      dicom_institution_name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      institution_type: 'hospital',
      timezone: 'Europe/Bucharest',
      default_language: 'ro',
      active: true
    }
  })

  const watchedName = watch('name')
  const watchedType = watch('institution_type')

  // Load tenants for selection (doar pentru admin)
  useEffect(() => {
    const loadTenants = async () => {
      // Dacă nu este admin, setează automat tenant-ul utilizatorului
      if (user?.role !== 'admin') {
        console.log('Operator detected, user:', user)
        console.log('tenant_id:', user?.tenant_id, 'tenant_name:', user?.tenant_name)
        
        if (user?.tenant_id && user?.tenant_name) {
          setTenants([{ id: user.tenant_id, name: user.tenant_name }])
          console.log('Tenants set:', [{ id: user.tenant_id, name: user.tenant_name }])
        } else {
          console.error('Missing tenant_id or tenant_name for operator!')
          toast.error('Eroare: Tenant ID sau nume lipsă. Vă rugăm reîncărcați pagina.')
        }
        return
      }

      // Pentru admin, încarcă toți tenants
      try {
        setLoadingTenants(true)
        const response = await apiClient.get<any[]>('/tenants/')
        setTenants(response.data || [])
      } catch (error) {
        console.error('Error loading tenants:', error)
        toast.error('Eroare la încărcarea tenant-ilor')
      } finally {
        setLoadingTenants(false)
      }
    }

    if (open) {
      loadTenants()
    }
  }, [open, user])

  // Auto-save draft la 30 secunde
  useEffect(() => {
    if (isDirty && !institution) {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer)
      }
      
      const timer = setTimeout(() => {
        handleAutoSave()
      }, 30000)
      
      setAutoSaveTimer(timer)
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer)
      }
    }
  }, [isDirty, watchedName, watchedType])

  // Load institution data when editing
  useEffect(() => {
    if (institution && open) {
      reset({
        name: institution.name || '',
        tenant_id: institution.tenant_id || '',
        dicom_institution_name: institution.dicom_institution_name || '',
        address: institution.address || '',
        phone: institution.phone || '',
        email: institution.email || '',
        website: institution.website || '',
        institution_type: institution.institution_type || 'hospital',
        timezone: institution.timezone || 'Europe/Bucharest',
        default_language: institution.default_language || 'ro',
        active: institution.active !== undefined ? institution.active : true
      })
    } else if (!institution && open) {
      // Pentru instituții noi, setează tenant_id automat pentru non-admin
      const defaultTenantId = user?.role !== 'admin' ? (user?.tenant_id || '') : ''
      console.log('Reset form for new institution, tenant_id:', defaultTenantId)
      
      reset({
        name: '',
        tenant_id: defaultTenantId,
        dicom_institution_name: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        institution_type: 'hospital',
        timezone: 'Europe/Bucharest',
        default_language: 'ro',
        active: true
      })
    }
  }, [institution, open, reset, user])

  const handleAutoSave = async () => {
    try {
      const formData = watch()
      // Aici ar trebui să salvez draft-ul în localStorage sau API
      localStorage.setItem('institution_draft', JSON.stringify(formData))
      setDraftSaved(true)
      setTimeout(() => setDraftSaved(false), 2000)
    } catch (error) {
      console.error('Eroare la auto-save:', error)
    }
  }

  const generateDicomName = () => {
    const name = watchedName?.toUpperCase().replace(/[^A-Z0-9]/g, '')
    const type = watchedType?.toUpperCase()
    return `${name}_${type}`.substring(0, 50)
  }

  const onSubmit = async (data: any) => {
    setSaving(true)
    try {
      if (institution) {
        await apiClient.put(`/tenants/institutions/${institution.id}`, data)
        toast.success('Instituția a fost actualizată cu succes!')
      } else {
        await apiClient.post('/tenants/institutions', data)
        toast.success('Instituția a fost creată cu succes!')
      }
      onSave()
      onClose()
    } catch (error: any) {
      console.error('Eroare la salvare:', error)
      toast.error(error.response?.data?.detail || 'Eroare la salvare')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAndContinue = async (data: any) => {
    setSaving(true)
    try {
      if (institution) {
        await apiClient.put(`/tenants/institutions/${institution.id}`, data)
        toast.success('Instituția a fost actualizată cu succes!')
      } else {
        const newInstitution = await apiClient.post('/tenants/institutions', data)
        toast.success('Instituția a fost creată cu succes!')
        // Reset form for next institution
        reset()
        // Update the institution prop for editing
        Object.assign(institution, newInstitution)
      }
      onSave()
    } catch (error: any) {
      console.error('Eroare la salvare:', error)
      toast.error(error.response?.data?.detail || 'Eroare la salvare')
    } finally {
      setSaving(false)
    }
  }

  // Dialog styling compact (ca QuickAddPatientForm)
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      fontSize: '0.8rem',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'primary.main',
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.85rem',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'divider',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },
    '& .MuiMenuItem-root': {
      fontSize: '0.8rem',
    },
  }

  return (
    <Dialog 
      open={open} 
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2,
        fontSize: '1rem',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}>
        {institution ? 'Editează Instituția' : 'Instituție Nouă'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5}>
            {/* Informații de bază */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Informații de bază
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }: any) => (
                  <TextField
                    {...field}
                    label="Nume instituție *"
                    fullWidth
                    size="small"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="tenant_id"
                control={control}
                render={({ field }: any) => (
                  <FormControl fullWidth size="small" error={!!errors.tenant_id}>
                    <InputLabel>Tenant *</InputLabel>
                    <Select
                      {...field}
                      label="Tenant *"
                      disabled={loadingTenants || user?.role !== 'admin'}
                    >
                      {tenants.map((tenant) => (
                        <MenuItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.tenant_id && (
                      <Typography variant="caption" color="error">
                        {errors.tenant_id.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="institution_type"
                control={control}
                render={({ field }: any) => (
                  <FormControl fullWidth size="small" error={!!errors.institution_type}>
                    <InputLabel>Tip instituție *</InputLabel>
                    <Select {...field} label="Tip instituție *">
                      {institutionTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="dicom_institution_name"
                control={control}
                render={({ field }: any) => (
                  <TextField
                    {...field}
                    label="Nume DICOM *"
                    fullWidth
                    size="small"
                    error={!!errors.dicom_institution_name}
                    helperText={errors.dicom_institution_name?.message}
                    placeholder={generateDicomName()}
                  />
                )}
              />
            </Grid>

            {/* Informații de contact */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Contact
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="address"
                control={control}
                render={({ field }: any) => (
                  <TextField
                    {...field}
                    label="Adresă"
                    fullWidth
                    size="small"
                    error={!!errors.address}
                    helperText={errors.address?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }: any) => (
                  <TextField
                    {...field}
                    label="Telefon"
                    fullWidth
                    size="small"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }: any) => (
                  <TextField
                    {...field}
                    label="Email"
                    size="small"
                    fullWidth
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="website"
                control={control}
                render={({ field }: any) => (
                  <TextField
                    {...field}
                    label="Website"
                    fullWidth
                    size="small"
                    error={!!errors.website}
                    helperText={errors.website?.message}
                  />
                )}
              />
            </Grid>

            {/* Configurări avansate */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Setări
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="timezone"
                control={control}
                render={({ field }: any) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Timezone</InputLabel>
                    <Select {...field} label="Timezone">
                      {timezones.map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="default_language"
                control={control}
                render={({ field }: any) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Limba</InputLabel>
                    <Select {...field} label="Limba">
                      {languages.map((lang) => (
                        <MenuItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Preview DICOM */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'background.default', 
                borderRadius: 1, 
                border: 1, 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography sx={{ fontSize: '0.78rem' }} color="text.secondary">
                  DICOM:
                </Typography>
                <Chip
                  label={generateDicomName() || 'N/A'}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          px: 2, 
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between'
        }}>
          <Controller
            name="active"
            control={control}
            render={({ field }: any) => (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                }
                label={<Typography sx={{ fontSize: '0.78rem' }}>Activă</Typography>}
                sx={{ ml: 0 }}
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={onClose} 
              disabled={saving}
              size="small"
              sx={{ fontSize: '0.8rem' }}
            >
              Anulează
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={saving}
              sx={{ fontSize: '0.8rem' }}
            >
              {saving ? <CircularProgress size={16} /> : 'Salvează'}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}
